import Vue from "vue"
import router from "@/router"

let laravelMutations = {
  /**
   * Writes provided list of items to the state and
   * sets the listLoaded flag to true
   *
   * @param {object} state - the module state
   * @param {object[]} value - List of items
   */
  SET_LIST (state, value) {
    Vue.set(state, "list", value)
    // state.list.splice(0)
    // state.list.push(...value)
    state.listLoaded = true
  },
  /**
     * Sets the sort param
     *
     * @param {object} state - the module state
     * @param {string} value - the sort value to persist
     */
  SET_SORT (state, value) {
    state.listSortParam = value
  },
  /**
     * Sets the row being edited
     *
     * @param {object} state - the module state
     * @param {string} value - the edit value to persist
     */
  SET_EDIT (state, value) {
    let clearEdit = () => {
      state.editId = null
      state.editRow = {}
    }
    if (value === null) return clearEdit()
    if (value === 0 || value === "new") {
      state.editId = 0
      state.editRow = { id: 0, ...state.editNewRow }
      return
    }
    let rowBeingEditedIndex = state.list.findIndex(e => e.id === value)
    if (rowBeingEditedIndex === -1) return clearEdit()
    state.editId = value
    state.editRow = { ...state.list[rowBeingEditedIndex] }
  },
  /**
     * Clears list of items
     */
  CLEAR_LIST (state) {
    state.list = []
    state.listLoaded = false
  },
  /**
     * Writes details of selected item to the state
     *
     * @param {object} state - the module state
     * @param {object} item - item details
     */
  SET_DETAILS (state, item) {
    state.detailsLoaded = true
    Vue.set(state, "details", item)
  },
  /**
     * Clears item details
     */
  CLEAR_DETAILS (state) {
    state.detailsLoaded = false
    state.details = {}
  },
  /**
     * Sets the edit row errors
     *
     * @param {object} state - the module state
     * @param {string} value - the errors to persist
     */
  SET_ERRORS (state, value) {
    state.errors = value
  },
  /**
     * Writes current pagination params to the state
     *
     * @param {object} state - the module state
     * @param {object} value - Latest pagination settings
     */
  SET_PAGINATION (state, value) {
    Vue.set(state, "pagination", {
      current_page: value.current_page,
      last_page: value.last_page,
      per_page: parseInt(value.per_page),
      total: value.total || 0,
      from: value.from || 0,
      to: value.to || 0
    })
  },
  /**
     * Writes filter options to the state
     *
     * @param {object} state - the module state
     * @param {string} module - The namespace of the module this filter is for
     * @param {string} key - The filter namespace
     * @param {object} params - Filter options to set
     */
  SET_FILTER (state, { module = null, key = null, params = null }) {
    if (module === null || key === null || params === null) return false

    let filters = JSON.parse(window.sessionStorage.getItem("filters")) || {}
    if (!filters[state.namespace]) filters[state.namespace] = {}
    if (!filters[state.namespace][module]) filters[state.namespace][module] = {}
    let currentStateFilter = filters[state.namespace][module]

    if (
      !params ||
      Object.keys(params).length === 0
    ) {
      delete currentStateFilter[key]
    } else {
      currentStateFilter[key] = params
    }

    window.sessionStorage.setItem("filters", JSON.stringify(filters))
    state.filters = filters[state.namespace]
  },
  /**
   * Removes a filter
   *
   * @param {object} state - the module state
   * @param {string} module - The module namespace
   * @param {string} key - The filter namespace
   */
  REMOVE_FILTER (state, { module, key }) {
    let filters = JSON.parse(window.sessionStorage.getItem("filters")) || {}
    if (!filters[state.namespace]) filters[state.namespace] = {}
    if (!filters[state.namespace][module]) filters[state.namespace][module] = {}

    let currentStateFilter = filters[state.namespace][module]

    if (currentStateFilter.hasOwnProperty(key)) delete currentStateFilter[key]

    window.sessionStorage.setItem("filters", JSON.stringify(filters))
    state.filters = filters[state.namespace]
  },
  /**
     * Clears filter options in the state
     *
     * @param {object} state - the module state
     * @param {object} routeName - The route we wish to clear the filters for
     */
  CLEAR_FILTERS (state, routeName) {
    if (routeName) state.filters[state.namespace][routeName] = {}
    state.filters[state.namespace] = {}
    window.sessionStorage.setItem("filters", JSON.stringify(state.filters))
  },
  /**
     * Load filters from sessionStorage to the state
     *
     * @param {object} state - the module state
     */
  LOAD_SESSIONSTORAGE_FILTERS (state) {
    let filters = JSON.parse(window.sessionStorage.getItem("filters")) || {}
    if (filters && filters[state.namespace]) state.filters = filters[state.namespace]
  },
  /**
     * Clears filter options in the state
     *
     * @param {object} state - the module state
     * @param {object} params - the params to set
     * @param {object} params.type - the tpye of loader to set
     * @param {object} params.value - the value to set
     * @param {object} params.key - (only for update and remove) the key to add/remove from array
     */
  SET_LOADER (state, params) {
    if (params.type === "update" || params.type === "remove" || (state.multipleLoadersTypes && state.multipleLoadersTypes.includes(params.type))) {
      if (params.value && !state.loaders[params.type].includes(params.key)) {
        state.loaders[params.type].push(params.key)
      } else if (!params.value && state.loaders[params.type].includes(params.key)) {
        state.loaders[params.type].splice(state.loaders[params.type].indexOf(params.key), 1)
      }
    } else {
      Vue.set(state.loaders, params.type, params.value)
    }
  }
}

let laravelActions = {
  /**
     * Retrieves list of items from api
     * commits mutations to write the list and pagination params to state
     *
     * @param {object} params - Filtering, sorting and pagination parameters
     *
     * @returns {object} Response status and data
     */
  async getList ({ rootState, state, commit }, params) {
    try {
      commit("SET_LOADER", { type: "list", value: true })
      let promise = await window.axios.get(`${rootState.apiUrl}${state.route}`, { params: {
        ...state.listDefaultParams,
        sort: state.listSortParam,
        filter: {
          ...state.filters[router.currentRoute.name],
          ...(params && params.hasOwnProperty("filter") ? params.filter : {})
        },
        per_page: process.env.VUE_APP_PAGE_SIZE,
        ...params
      } })
      commit("SET_LIST", promise.data.data)
      commit("SET_PAGINATION", promise.data)
      commit("SET_LOADER", { type: "list", value: false })
      return { status: promise.status, data: promise.data.data }
    } catch (error) {
      commit("SET_LOADER", { type: "list", value: false })
      return { ...error.response }
    }
  },
  /**
   * Commits the mutation that clears the list
   */
  async clearList ({ commit }) {
    commit("CLEAR_LIST")
  },
  /**
   * Sets the sort param
   *
   * @param {string} param - the sort param to set
   */
  setSort ({ commit }, param) {
    commit("SET_SORT", param)
  },
  /**
     * Sets the filter param
     *
     * @param {string} param - the filter params to set
     */
  setFilter ({ commit }, param) {
    commit("SET_FILTER", param)
  },
  /**
     * Removes a filter
     *
     * @param {string} param - the filter namespace to remove
     */
  removeFilter ({ commit }, param) {
    commit("REMOVE_FILTER", param)
  },
  /**
     * Removes all filters
     */
  clearFilters ({ commit }) {
    commit("CLEAR_FIlTERS")
  },
  /**
     * Clears the sort param
     */
  clearSort ({ commit }) {
    commit("SET_SORT", "")
  },
  /**
   * Sets the edit param
   *
   * @param {string} param - the edit param to set
   */
  setEdit ({ commit }, param) {
    commit("SET_EDIT", param)
  },
  /**
   * Clears the edit param
   */
  clearEdit ({ commit }) {
    commit("SET_EDIT", null)
  },
  /**
     * Stores the edit item
     *
     * @returns {object} Store action response
     */
  async storeEditItem ({ dispatch, state }) {
    let params = {};
    ["id", ...Object.keys(state.editNewRow)].forEach(e => {
      params[e] = state.editRow[e]
    })
    let res
    if (params.id === 0) {
      res = await dispatch("store", params)
    } else {
      res = await dispatch("update", { params, id: state.editRow.id })
    }
    if (res.status === 200 || res.status === 201) dispatch("clearEdit")
    return res
  },
  /**
     * Retrieves item details from api
     * commits mutations to write the details to state
     *
     * @param {number} id - Id of the item we want to get details for
     *
     * @returns {object} Response status and data
     */
  async getDetails ({ rootState, commit, state }, { id, params }) {
    try {
      commit("SET_LOADER", { type: "details", value: id })
      let promise = await window.axios.get(`${rootState.apiUrl}${state.route}/${id}`, { params: {
        ...state.detailsDefaultParams,
        ...params
      } })
      commit("SET_DETAILS", promise.data)
      commit("SET_LOADER", { type: "details", value: false })
      return { status: promise.status, data: promise.data }
    } catch (error) {
      commit("SET_LOADER", { type: "details", value: false })
      return { ...error.response }
    }
  },
  /**
   * Commits the mutation that clears the details object
   */
  async clearDetails ({ commit }) {
    commit("CLEAR_DETAILS")
  },
  /**
     * Creates a new item record from provided params
     *
     * @param {object} params - item info to be stored
     *
     * @returns {object} Response status and data
     */
  async store ({ rootState, state, commit }, params) {
    try {
      commit("SET_ERRORS", {})
      commit("SET_LOADER", { type: "store", value: true })
      let promise = await window.axios.post(`${rootState.apiUrl}${state.route}`, params)
      commit("SET_LOADER", { type: "store", value: false })
      return { status: promise.status, data: promise.data }
    } catch (error) {
      commit("SET_LOADER", { type: "store", value: false })
      let errors = {}
      Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
      commit("SET_ERRORS", errors)
      return { ...error.response }
    }
  },
  /**
     * Updates a item record with provided params
     *
     * @param {number} id - Id of the item we wish to update
     * @param {object} params - Params we wish to update the record with
     *
     * @returns {object} Response status and data
     */
  async update ({ rootState, state, commit }, { id, params }) {
    try {
      commit("SET_ERRORS", {})
      commit("SET_LOADER", { type: "update", key: id, value: true })
      let promise = await window.axios.put(`${rootState.apiUrl}${state.route}/${id}`, params)
      commit("SET_LOADER", { type: "update", key: id, value: false })
      return { status: promise.status, data: promise.data }
    } catch (error) {
      commit("SET_LOADER", { type: "update", key: id, value: false })
      let errors = {}
      Object.keys(error.response.data.errors).forEach(e => { errors[e] = error.response.data.errors[e][0] })
      commit("SET_ERRORS", errors)
      return { ...error.response }
    }
  },
  /**
   * Sets the errors object
   *
   * @param {string} param - the errors object to set
   */
  setErrors ({ commit }, param) {
    commit("SET_ERRORS", param)
  },
  /**
   * Clears the errors object
   */
  clearErrors ({ commit }) {
    commit("SET_ERRORS", {})
  },
  /**
     * Deletes a item record
     *
     * @param {object} context - The vuex context object
     * @param {object} context.state - The module state
     * @param {number} id - Id pf the item record we wish to delete
     *
     * @returns {object} Response status and data
     */
  async remove ({ rootState, state, commit }, id) {
    try {
      commit("SET_LOADER", { type: "remove", key: id, value: true })
      let promise = await window.axios.delete(`${rootState.apiUrl}${state.route}/${id}`)
      commit("SET_LOADER", { type: "remove", key: id, value: false })
      return { status: promise.status, data: promise.data }
    } catch (error) {
      commit("SET_LOADER", { type: "remove", key: id, value: false })
      return { ...error.response }
    }
  }
}

export default (() => {
  window.generateLaravelResource = ({
    /**
     * Defines the route prefix for making API calls
     * @type { String }
     */
    route,
    /**
     * Will override the namespaced option if provided
     * @type { Boolean }
     */
    namespaced,
    /**
     * Will override/extend the default state if provided
     * @type { Object }
     */
    state,
    /**
     * Will override/extend the default getters if provided
     * @type { Object }
     */
    getters,
    /**
     * Will override/extend the default mutations if provided
     * @type { Object }
     */
    mutations,
    /**
     * Will override/extend the default actions if provided
     * @type { Object }
     */
    actions,
    /**
     * Will override/extend the default plugins if provided
     * @type { Object }
     */
    plugins
  }) => {
    return {
      namespaced: namespaced || true,
      plugins,
      // strict: true,
      state: {
        namespace: "Resource",
        /**
          * The route that the resource is getting and sending data to/from
          */
        route: route,
        /**
          * Stores an array of resource item objects
          */
        list: [],
        /**
          * Is the list already loaded?
          * (Used for differentiating between default state and empty list)
          */
        listLoaded: false,
        /**
          * The sort parameter to be sent with getList (index) request
          */
        listSortParam: null,
        /**
          * The default parameters to be sent with getList (index) request
          */
        listDefaultParams: {},
        /**
          * The id of the item being edited in edit row,
          * if null no item is being edited, if 0 new item is being created
          */
        editId: null,
        /**
          * The data for the edit row being edited/created
          */
        editRow: {},
        /**
          * The template of a new row (also used when extracting edit params)
          */
        editNewRow: {},
        /**
                 * Holds the selected filter options
                 */
        filters: {},
        /**
          * Stores details of a selected item
          */
        details: {},
        /**
          * Keeps track of wether the details have been loaded
          */
        detailsLoaded: false,
        /**
          * The default parameters to be sent with getDetails (show) request
          */
        detailsDefaultParams: {},
        /**
          * Stores list pagination settings
          */
        pagination: {},
        /**
          * The input error messages that are returned when trying to store or update
          */
        errors: {},
        /**
          * A list of loaders that turn on and off when requests are made (arrays hold item ids)
          */
        loaders: {
          list: false,
          details: false,
          store: false,
          update: [],
          remove: []
        },
        ...state
      },
      getters: {
        GetTableList (state) {
          if (state.editId === null) return state.list
          if (state.editId === 0) return [state.editRow, ...state.list]
          if (state.editId) {
            let rowBeingEditedIndex = state.list.findIndex(e => e.id === state.editId)
            let output = [...state.list]
            output.splice(rowBeingEditedIndex, 1, state.editRow)
            return output
          }
          return state.list
        },
        ...getters
      },
      mutations: {
        ...laravelMutations,
        ...mutations
      },
      actions: {
        ...laravelActions,
        ...actions
      }
    }
  }
})()
